// SPDX-License-Identifier: AGPL-3.0-or-later

import GatewayConnection from '@app/features/gateway/transport/GatewayConnection';
import {Logger} from '@app/features/platform/utils/AppLogger';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import {AudioPlaybackPermissionModal} from '@app/features/voice/components/modals/AudioPlaybackPermissionModal';
import {VoiceReceiveHealthMonitor} from '@app/features/voice/components/VoiceReceiveHealthMonitor';
import MediaEngine, {useMediaEngineVersion} from '@app/features/voice/engine/MediaEngineFacade';
import {
	initializeDesktopMediaPreferences,
	watchDesktopMediaPreferenceDevices,
} from '@app/features/voice/state/DesktopMediaPreferencesSync';
import {recordVoiceRecoveryDiagnostic} from '@app/features/voice/state/VoiceRecoveryDiagnostics';
import {useAudioPlayback} from '@livekit/components-react';
import type {Room} from 'livekit-client';
import {observer} from 'mobx-react-lite';
import {useEffect, useRef, useState} from 'react';

const logger = new Logger('VoiceReconnectionManager');
const AutoReconnectHandler = observer(() => {
	useMediaEngineVersion();
	const socket = GatewayConnection.socket;
	const isRoomConnected = MediaEngine.connected;
	const isRoomReconnecting = MediaEngine.reconnecting;
	const hasAttemptedReconnection = useRef(false);
	const lastChannel = MediaEngine.getLastConnectedChannel();
	const shouldReconnect = MediaEngine.getShouldReconnect();
	const lastChannelGuildId = lastChannel?.guildId ?? null;
	const lastChannelId = lastChannel?.channelId ?? null;
	useEffect(() => {
		if (!socket || hasAttemptedReconnection.current) {
			return;
		}
		if (MediaEngine.connected || MediaEngine.reconnecting) {
			logger.info('Voice room is connected or recovering; skipping channel rejoin');
			return;
		}
		if (!lastChannelGuildId || !lastChannelId || !shouldReconnect) {
			return;
		}
		const lastChannel = {guildId: lastChannelGuildId, channelId: lastChannelId};
		logger.info('Attempting to reconnect to last voice channel', lastChannel);
		hasAttemptedReconnection.current = true;
		const timerId = setTimeout(() => {
			const stillShouldReconnect = MediaEngine.getShouldReconnect();
			if (stillShouldReconnect && !MediaEngine.connected && !MediaEngine.reconnecting) {
				MediaEngine.connectToVoiceChannel(lastChannel.guildId, lastChannel.channelId);
				MediaEngine.markReconnectionAttempted();
			} else {
				logger.info('Voice channel rejoin is no longer needed');
			}
		}, 1500);
		return () => clearTimeout(timerId);
	}, [isRoomConnected, isRoomReconnecting, lastChannelGuildId, lastChannelId, shouldReconnect, socket]);
	return null;
});
const AudioPlaybackHandler = observer(({room}: {room: Room}) => {
	useMediaEngineVersion();
	const {canPlayAudio, startAudio} = useAudioPlayback(room);
	const audioContext = Reflect.get(room, 'audioContext') as AudioContext | undefined;
	const isVoiceConnected = MediaEngine.connected;
	const [audioContextState, setAudioContextState] = useState<AudioContextState | 'unknown'>(audioContext?.state ?? 'unknown');
	const hasShownAudioModal = useRef(false);
	const needsResumeGesture = !canPlayAudio || audioContextState === 'suspended';
	useEffect(() => {
		if (!audioContext) {
			setAudioContextState('unknown');
			return;
		}
		const updateState = () => setAudioContextState(audioContext.state);
		updateState();
		audioContext.addEventListener('statechange', updateState);
		return () => audioContext.removeEventListener('statechange', updateState);
	}, [audioContext]);
	useEffect(() => {
		if (!needsResumeGesture) {
			hasShownAudioModal.current = false;
			return;
		}
		if (hasShownAudioModal.current) {
			return;
		}
		if (isVoiceConnected) {
			hasShownAudioModal.current = true;
			recordVoiceRecoveryDiagnostic('playback-suspended');
			logger.info('Local audio playback needs a user gesture');
			ModalCommands.pushWithKey(
				modal(() => (
					<AudioPlaybackPermissionModal
						onStartAudio={async () => {
							try {
								const resumePlayback = startAudio();
								const resumeContext = audioContext?.state === 'suspended' ? audioContext.resume() : Promise.resolve();
								await Promise.all([resumePlayback, resumeContext]);
								recordVoiceRecoveryDiagnostic('recovered');
								logger.info('Local audio playback resumed');
							} catch (error) {
								recordVoiceRecoveryDiagnostic('unrecoverable');
								logger.error('Failed to resume local audio playback', {errorName: error instanceof Error ? error.name : 'Error'});
							}
						}}
						data-flx="voice.voice-reconnection-manager.audio-playback-handler.audio-playback-permission-modal"
					/>
				)),
				'audio-playback-permission',
			);
		}
	}, [audioContext, canPlayAudio, isVoiceConnected, needsResumeGesture, startAudio]);
	return null;
});
export const VoiceReconnectionManager = observer(() => {
	useMediaEngineVersion();
	const room = MediaEngine.room;
	const preferencesApi = globalThis.window?.electron?.mediaPreferences;
	const [desktopPreferencesReady, setDesktopPreferencesReady] = useState(!preferencesApi);
	useEffect(() => {
		if (!preferencesApi) {
			setDesktopPreferencesReady(true);
			return;
		}
		let active = true;
		const stopWatchingDevices = watchDesktopMediaPreferenceDevices();
		const timeout = setTimeout(() => {
			if (active) setDesktopPreferencesReady(true);
		}, 2500);
		void initializeDesktopMediaPreferences().finally(() => {
			clearTimeout(timeout);
			if (active) setDesktopPreferencesReady(true);
		});
		return () => {
			active = false;
			clearTimeout(timeout);
			stopWatchingDevices();
		};
	}, [preferencesApi]);
	return (
		<>
			{desktopPreferencesReady ? <AutoReconnectHandler data-flx="voice.voice-reconnection-manager.auto-reconnect-handler" /> : null}
			{room && <AudioPlaybackHandler room={room} data-flx="voice.voice-reconnection-manager.audio-playback-handler" />}
			{room && <VoiceReceiveHealthMonitor room={room} />}
		</>
	);
});
